import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { setFileAttachmentMeta } from "@f2e/shared/utils";
const MIME_TYPE_CANDIDATES = [
    "audio/mp4",
    "audio/webm;codecs=opus",
    "audio/webm",
];
export const DEFAULT_VOICE_RECORDING_MAX_MS = 5 * 60 * 1000;
export const DEFAULT_VOICE_RECORDING_BITS_PER_SECOND = 64000;
const RECORDING_DATA_TIMESLICE_MS = 1000;
const RECORDING_STOP_FALLBACK_MS = 3000;
const WAVEFORM_SAMPLE_COUNT = 30;
const MIN_WAVEFORM_LEVEL = 0.08;
export const VoiceRecorderErrorCode = {
    Unsupported: "unsupported",
    PermissionDenied: "permission_denied",
    EmptyRecording: "empty_recording",
    StartFailed: "start_failed",
};
export class VoiceRecorderError extends Error {
    code;
    constructor(code, message) {
        super(message);
        this.name = "VoiceRecorderError";
        this.code = code;
    }
}
const normalizeRecordingMimeType = (mimeType) => {
    const normalized = mimeType?.split(";", 1)[0]?.trim().toLowerCase();
    return normalized || null;
};
const getRecorderConstructor = () => {
    if (typeof globalThis === "undefined")
        return null;
    return typeof globalThis.MediaRecorder === "undefined"
        ? null
        : globalThis.MediaRecorder;
};
export const getPreferredVoiceRecordingMimeType = () => {
    const recorderConstructor = getRecorderConstructor();
    if (!recorderConstructor?.isTypeSupported)
        return undefined;
    return MIME_TYPE_CANDIDATES.find((mimeType) => recorderConstructor.isTypeSupported(mimeType));
};
const isVoiceRecordingSupported = () => {
    return Boolean(getRecorderConstructor() && globalThis.navigator?.mediaDevices?.getUserMedia);
};
const getAudioContextConstructor = () => {
    const globalWithWebkitAudio = globalThis;
    return (globalWithWebkitAudio.AudioContext ??
        globalWithWebkitAudio.webkitAudioContext ??
        null);
};
const createInitialWaveformSamples = () => Array.from({ length: WAVEFORM_SAMPLE_COUNT }, () => MIN_WAVEFORM_LEVEL);
const getNormalizedVolumeLevel = (data) => {
    if (data.length === 0)
        return MIN_WAVEFORM_LEVEL;
    let sumSquares = 0;
    data.forEach((value) => {
        const centered = (value - 128) / 128;
        sumSquares += centered * centered;
    });
    const rms = Math.sqrt(sumSquares / data.length);
    return Math.max(MIN_WAVEFORM_LEVEL, Math.min(1, rms * 4));
};
export const useVoiceRecorder = ({ maxDurationMs = DEFAULT_VOICE_RECORDING_MAX_MS, audioBitsPerSecond = DEFAULT_VOICE_RECORDING_BITS_PER_SECOND, onRecordingComplete, onError, } = {}) => {
    const [isStarting, setIsStarting] = useState(false);
    const [isRecording, setIsRecording] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);
    const [elapsedMs, setElapsedMs] = useState(0);
    const [waveformSamples, setWaveformSamples] = useState(createInitialWaveformSamples);
    const recorderRef = useRef(null);
    const streamRef = useRef(null);
    const chunksRef = useRef([]);
    const timeoutRef = useRef(null);
    const stopFallbackTimeoutRef = useRef(null);
    const elapsedIntervalRef = useRef(null);
    const waveformIntervalRef = useRef(null);
    const audioContextRef = useRef(null);
    const audioSourceRef = useRef(null);
    const recordingStartedAtRef = useRef(null);
    const recordingDurationMsRef = useRef(null);
    const startingRef = useRef(false);
    const mountedRef = useRef(true);
    const onRecordingCompleteRef = useRef(onRecordingComplete);
    const onErrorRef = useRef(onError);
    const finalizedRef = useRef(false);
    onRecordingCompleteRef.current = onRecordingComplete;
    onErrorRef.current = onError;
    const isSupported = useMemo(isVoiceRecordingSupported, []);
    const clearRecordingTimeout = useCallback(() => {
        if (!timeoutRef.current)
            return;
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
    }, []);
    const clearStopFallbackTimeout = useCallback(() => {
        if (!stopFallbackTimeoutRef.current)
            return;
        clearTimeout(stopFallbackTimeoutRef.current);
        stopFallbackTimeoutRef.current = null;
    }, []);
    const clearElapsedInterval = useCallback(() => {
        if (!elapsedIntervalRef.current)
            return;
        clearInterval(elapsedIntervalRef.current);
        elapsedIntervalRef.current = null;
    }, []);
    const clearWaveformInterval = useCallback(() => {
        if (!waveformIntervalRef.current)
            return;
        clearInterval(waveformIntervalRef.current);
        waveformIntervalRef.current = null;
    }, []);
    const cleanupAudioAnalyzer = useCallback(() => {
        clearWaveformInterval();
        audioSourceRef.current?.disconnect();
        audioSourceRef.current = null;
        const audioContext = audioContextRef.current;
        audioContextRef.current = null;
        if (audioContext && audioContext.state !== "closed") {
            void audioContext.close().catch(() => {
                // Ignore close failures; recording cleanup should not surface analyzer errors.
            });
        }
    }, [clearWaveformInterval]);
    const resetElapsed = useCallback(() => {
        recordingStartedAtRef.current = null;
        clearElapsedInterval();
        setElapsedMs(0);
    }, [clearElapsedInterval]);
    const resetWaveformSamples = useCallback(() => {
        setWaveformSamples(createInitialWaveformSamples());
    }, []);
    const stopStreamTracks = useCallback(() => {
        streamRef.current?.getTracks().forEach((track) => track.stop());
        streamRef.current = null;
    }, []);
    const resetRecorder = useCallback(() => {
        recorderRef.current = null;
        chunksRef.current = [];
        clearRecordingTimeout();
        clearStopFallbackTimeout();
        cleanupAudioAnalyzer();
        stopStreamTracks();
    }, [
        cleanupAudioAnalyzer,
        clearRecordingTimeout,
        clearStopFallbackTimeout,
        stopStreamTracks,
    ]);
    const emitError = useCallback((error) => {
        onErrorRef.current?.(error);
    }, []);
    const finishRecording = useCallback((recorder, preferredMimeType) => {
        if (finalizedRef.current)
            return;
        finalizedRef.current = true;
        clearStopFallbackTimeout();
        const recordingMimeType = normalizeRecordingMimeType(recorder.mimeType || preferredMimeType);
        const chunks = [...chunksRef.current];
        resetRecorder();
        if (!mountedRef.current)
            return;
        setIsProcessing(false);
        if (!recordingMimeType) {
            emitError(new VoiceRecorderError(VoiceRecorderErrorCode.StartFailed, "Failed to determine recording format."));
            return;
        }
        const blob = new Blob(chunks, { type: recordingMimeType });
        if (blob.size === 0) {
            recordingDurationMsRef.current = null;
            emitError(new VoiceRecorderError(VoiceRecorderErrorCode.EmptyRecording, "Recording did not contain audio."));
            return;
        }
        const extension = recordingMimeType === "audio/mp4" ? "m4a" : "webm";
        const durationMs = recordingDurationMsRef.current ??
            (recordingStartedAtRef.current
                ? Math.max(0, Date.now() - recordingStartedAtRef.current)
                : elapsedMs);
        recordingDurationMsRef.current = null;
        const file = new File([blob], `voice-message-${Date.now()}.${extension}`, {
            type: recordingMimeType,
        });
        setFileAttachmentMeta(file, {
            durationMs,
            sizeBytes: file.size,
            previewKind: "audio",
        });
        onRecordingCompleteRef.current?.(file);
    }, [clearStopFallbackTimeout, elapsedMs, emitError, resetRecorder]);
    const failRecording = useCallback((message = "Failed to start voice recording.") => {
        finalizedRef.current = true;
        startingRef.current = false;
        resetRecorder();
        if (mountedRef.current) {
            setIsStarting(false);
            setIsRecording(false);
            setIsProcessing(false);
            resetElapsed();
            resetWaveformSamples();
        }
        emitError(new VoiceRecorderError(VoiceRecorderErrorCode.StartFailed, message));
    }, [emitError, resetElapsed, resetRecorder, resetWaveformSamples]);
    const stopRecording = useCallback(() => {
        const recorder = recorderRef.current;
        if (!recorder || recorder.state === "inactive")
            return;
        recordingDurationMsRef.current = recordingStartedAtRef.current
            ? Math.max(0, Date.now() - recordingStartedAtRef.current)
            : elapsedMs;
        setIsRecording(false);
        setIsProcessing(true);
        clearRecordingTimeout();
        resetElapsed();
        resetWaveformSamples();
        clearWaveformInterval();
        stopFallbackTimeoutRef.current = setTimeout(() => {
            finishRecording(recorder, recorder.mimeType);
        }, RECORDING_STOP_FALLBACK_MS);
        try {
            recorder.requestData?.();
            recorder.stop();
        }
        catch (error) {
            clearStopFallbackTimeout();
            failRecording(error instanceof Error
                ? error.message
                : "Failed to stop voice recording.");
        }
    }, [
        clearStopFallbackTimeout,
        clearRecordingTimeout,
        clearWaveformInterval,
        elapsedMs,
        failRecording,
        finishRecording,
        resetElapsed,
        resetWaveformSamples,
    ]);
    const startAudioAnalyzer = useCallback((stream) => {
        const AudioContextConstructor = getAudioContextConstructor();
        if (!AudioContextConstructor)
            return;
        try {
            cleanupAudioAnalyzer();
            const audioContext = new AudioContextConstructor();
            const analyser = audioContext.createAnalyser();
            analyser.fftSize = 256;
            analyser.smoothingTimeConstant = 0.75;
            const source = audioContext.createMediaStreamSource(stream);
            source.connect(analyser);
            audioContextRef.current = audioContext;
            audioSourceRef.current = source;
            const data = new Uint8Array(analyser.fftSize);
            waveformIntervalRef.current = setInterval(() => {
                analyser.getByteTimeDomainData(data);
                const level = getNormalizedVolumeLevel(data);
                setWaveformSamples((previous) => [...previous.slice(1), level]);
            }, 100);
        }
        catch {
            cleanupAudioAnalyzer();
        }
    }, [cleanupAudioAnalyzer]);
    const startRecording = useCallback(async () => {
        if (!isSupported) {
            emitError(new VoiceRecorderError(VoiceRecorderErrorCode.Unsupported, "Voice recording is not supported in this browser."));
            return;
        }
        if (startingRef.current || recorderRef.current?.state === "recording")
            return;
        try {
            startingRef.current = true;
            setIsStarting(true);
            chunksRef.current = [];
            recordingDurationMsRef.current = null;
            finalizedRef.current = false;
            clearStopFallbackTimeout();
            resetWaveformSamples();
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            if (!mountedRef.current) {
                startingRef.current = false;
                stream.getTracks().forEach((track) => track.stop());
                return;
            }
            streamRef.current = stream;
            startAudioAnalyzer(stream);
            const preferredMimeType = getPreferredVoiceRecordingMimeType();
            const recorder = new MediaRecorder(stream, {
                ...(preferredMimeType ? { mimeType: preferredMimeType } : {}),
                audioBitsPerSecond,
            });
            recorderRef.current = recorder;
            recorder.ondataavailable = (event) => {
                if (event.data.size > 0) {
                    chunksRef.current.push(event.data);
                }
            };
            recorder.onerror = () => {
                failRecording("Failed to record audio.");
            };
            recorder.onstop = () => finishRecording(recorder, preferredMimeType);
            recorder.start(RECORDING_DATA_TIMESLICE_MS);
            startingRef.current = false;
            setIsStarting(false);
            setIsRecording(true);
            setIsProcessing(false);
            setElapsedMs(0);
            recordingStartedAtRef.current = Date.now();
            clearElapsedInterval();
            elapsedIntervalRef.current = setInterval(() => {
                if (!recordingStartedAtRef.current)
                    return;
                setElapsedMs(Date.now() - recordingStartedAtRef.current);
            }, 250);
            timeoutRef.current = setTimeout(stopRecording, maxDurationMs);
        }
        catch (error) {
            startingRef.current = false;
            setIsStarting(false);
            resetRecorder();
            if (mountedRef.current) {
                setIsRecording(false);
                setIsProcessing(false);
                resetElapsed();
                resetWaveformSamples();
            }
            const code = error instanceof DOMException && error.name === "NotAllowedError"
                ? VoiceRecorderErrorCode.PermissionDenied
                : VoiceRecorderErrorCode.StartFailed;
            const message = code === VoiceRecorderErrorCode.PermissionDenied
                ? "Microphone permission was denied."
                : "Failed to start voice recording.";
            emitError(new VoiceRecorderError(code, message));
        }
    }, [
        audioBitsPerSecond,
        clearStopFallbackTimeout,
        emitError,
        failRecording,
        finishRecording,
        isSupported,
        maxDurationMs,
        resetRecorder,
        clearElapsedInterval,
        resetElapsed,
        resetWaveformSamples,
        startAudioAnalyzer,
        stopRecording,
    ]);
    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
            startingRef.current = false;
            clearRecordingTimeout();
            clearStopFallbackTimeout();
            recordingStartedAtRef.current = null;
            clearElapsedInterval();
            cleanupAudioAnalyzer();
            const recorder = recorderRef.current;
            if (recorder && recorder.state !== "inactive") {
                recorder.onstop = null;
                recorder.ondataavailable = null;
                recorder.stop();
            }
            resetRecorder();
        };
    }, [
        cleanupAudioAnalyzer,
        clearElapsedInterval,
        clearRecordingTimeout,
        clearStopFallbackTimeout,
        resetRecorder,
    ]);
    return {
        isSupported,
        isStarting,
        isRecording,
        isProcessing,
        elapsedMs,
        waveformSamples,
        startRecording,
        stopRecording,
    };
};
//# sourceMappingURL=useVoiceRecorder.js.map